import { IRentedPropertyPhoto } from "../types/ownerDashboard.types";
import { extractPhotoCollection } from "./extractPhotoCollection";

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function asNullableString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function asFiniteNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function hasPhotoUrl(photo: IRentedPropertyPhoto): boolean {
  return Boolean(photo.url_s || photo.url_m || photo.url_l);
}

function resolvePhotoUrls(
  record: Record<string, unknown>
): Pick<IRentedPropertyPhoto, "url_s" | "url_m" | "url_l"> {
  const url_s = asNullableString(
    record.url_s ?? record.urlS ?? record.url_small
  );
  const url_m = asNullableString(
    record.url_m ?? record.urlM ?? record.url_medium
  );
  const url_l = asNullableString(
    record.url_l ?? record.urlL ?? record.url_large
  );
  const fallbackUrl = asNullableString(record.url);

  return {
    url_s: url_s ?? fallbackUrl,
    url_m: url_m ?? fallbackUrl ?? url_l ?? url_s,
    url_l: url_l ?? fallbackUrl ?? url_m ?? url_s,
  };
}

export function normalizeRentedPropertyPhoto(
  value: unknown
): IRentedPropertyPhoto | null {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const id = asFiniteNumber(record.id);
  if (id == null || id <= 0) {
    return null;
  }

  const photo: IRentedPropertyPhoto = {
    id,
    ...resolvePhotoUrls(record),
  };

  return hasPhotoUrl(photo) ? photo : null;
}

export function normalizeRentedPropertyPhotos(
  photos: unknown
): IRentedPropertyPhoto[] {
  return extractPhotoCollection(photos)
    .map((photo) => normalizeRentedPropertyPhoto(photo))
    .filter((photo): photo is IRentedPropertyPhoto => photo != null);
}
